import { isFulfilled } from "@reduxjs/toolkit";
import { format, getMonth, getYear } from "date-fns";
import { FieldValues, FormProvider, useForm } from "react-hook-form";
import { useAppDispatch, useAppSelector } from "../../../../../../app/hooks";
import DialogContentWrapper from "../../../../../../common/components/functional/dialogs/DialogContentWrapper";
import OverlaySpinner from "../../../../../../common/components/functional/overlaySpinner/OverlaySpinner";
import { Nullable } from "../../../../../../common/types/nullable";
import { AccumulationsForEmploymentResponse } from "../../../../../employment/employmentApi";
import { addAccumulationDelta } from "../../../../../employment/employmentSlice";
import {
  AccumulationField,
  AccumulationType,
  EventSummary,
  SelectedAccumulation,
} from "../../types";
import AccumulationsEditFormFields from "./AccumulationsEditFormFields";

type Props = {
  accumulationType: AccumulationType;
  employmentId?: string;
  contEmpId?: string;
  onClose: () => void;
  tableData: AccumulationsForEmploymentResponse;
  updateLocalTableData: (date: string, data: EventSummary[]) => void;
  selectedAccumulation: Nullable<SelectedAccumulation>;
  getFields: (
    tableData: AccumulationsForEmploymentResponse,
    selectedAccumulation: SelectedAccumulation,
  ) => [AccumulationField[], AccumulationField[]];
  canEdit: boolean;
};

const getDefaultValues = (
  keys: AccumulationField[],
  stateNames: AccumulationField[],
): Record<string, string> => {
  const obj: Record<string, string> = {};

  // object with empty strings for each key as default values
  [...keys, ...stateNames].forEach((key) => (obj[key.name] = ""));
  return obj;
};

const AccumulationsEditForm = ({
  accumulationType,
  employmentId,
  contEmpId,
  onClose,
  tableData,
  selectedAccumulation,
  getFields,
  updateLocalTableData,
  canEdit,
}: Props) => {
  const dispatch = useAppDispatch();
  const { status } = useAppSelector((state) => state.employment);
  const isLoading = status === "pending";

  const [keys, stateNames] = getFields(
    tableData,
    selectedAccumulation as SelectedAccumulation,
  );

  const fields = [...keys, ...stateNames];

  const onSave = (fieldValues: FieldValues) => {
    if (!canEdit) return;

    const { modificationDate, month, year, custOrgId, assignmentId, ...rest } =
      fieldValues;

    const yearString = getYear(fieldValues?.year).toString();
    const monthString = (getMonth(fieldValues?.month) + 1).toString();
    const date = format(modificationDate, "yyyy-MM-dd");
    const addAccumDelta = {
      isInternal: false,
      date,
      contEmpId,
      ...(accumulationType === "employment" && { employmentId }),
      ...(fieldValues?.year && { year: yearString }),
      ...(fieldValues?.month && { month: monthString }),
      ...(fieldValues?.custOrgId && { custOrgId: fieldValues.custOrgId?.id }),
      ...(assignmentId && { assignmentId: assignmentId.id }),
      values: {
        ...rest, // stateNames
      },
    };

    dispatch(addAccumulationDelta(addAccumDelta)).then((action) => {
      if (isFulfilled(action)) {
        const eventPayload = action.payload.map((item) => ({
          ...(item.payload.month && { month: item.payload.month }),
          ...(item.payload.year && { year: item.payload.year }),
          // show assignment name instead of id in table after saving
          ...(fieldValues?.assignmentId && { assignmentId: assignmentId.name }),
          ...(item.payload.customerOrgId && {
            custOrgId: custOrgId.name,
          }),
          [item.payload.stateName]: item.payload.value,
          deltaId: item.id,
        }));

        updateLocalTableData(date, eventPayload);
        onClose();
      }
    });
  };

  const methods = useForm({
    defaultValues: {
      modificationDate: new Date(),
      ...getDefaultValues(keys, stateNames),
    },
    mode: "onChange",
  });

  const {
    handleSubmit,
    formState: { isDirty, isValid },
  } = methods;

  return (
    <DialogContentWrapper
      title="Korjaa kertymää"
      margin
      onCancel={onClose}
      onSave={handleSubmit(onSave)}
      customSaveButtonLabel="Tallenna"
      saveDisabled={!isDirty || !isValid}
      containerSx={{ width: "800px" }}
    >
      <FormProvider {...methods}>
        <AccumulationsEditFormFields
          fields={fields}
          employmentId={employmentId as string}
        />
        <OverlaySpinner isLoading={isLoading} sx={{ position: "absolute" }} />
      </FormProvider>
    </DialogContentWrapper>
  );
};

export default AccumulationsEditForm;
